import React from "react";
import { Row, Col, Form, Alert } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaBriefcase,
  FaBuilding,
  FaUserTie,
  FaDollarSign,
  FaChartLine,
  FaPiggyBank,
  FaExclamationTriangle,
} from "react-icons/fa";

const EmploymentAndFinancialStep = ({
  formData,
  updateFormData,
  errors,
  nextStep,
  prevStep,
  currentStep,
}) => {
  const employment = formData.employment || {};
  const financial = formData.financial || {};

  const handleEmploymentChange = (e) => {
    const { name, value } = e.target;
    updateFormData("employment", { [name]: value });
  };

  const handleFinancialChange = (e) => {
    const { name, value } = e.target;
    updateFormData("financial", { [name]: value });
  };

  const employmentStatusOptions = [
    { value: "", label: "Select employment status" },
    { value: "employed", label: "Employed (Full-time)" },
    { value: "employed-part", label: "Employed (Part-time)" },
    { value: "self-employed", label: "Self-employed / Freelance" },
    { value: "business-owner", label: "Business Owner" },
    { value: "retired", label: "Retired" },
    { value: "unemployed", label: "Unemployed" },
    { value: "student", label: "Student" },
    { value: "other", label: "Other" },
  ];

  const occupationCategories = [
    "Accounting/Finance",
    "Administrative/Clerical",
    "Agriculture/Farming",
    "Arts/Entertainment/Media",
    "Aviation",
    "Banking/Financial Services",
    "Construction/Trades",
    "Consulting",
    "Education/Teaching",
    "Engineering",
    "Government/Public Service",
    "Healthcare/Medical",
    "Hospitality/Tourism",
    "Information Technology",
    "Insurance",
    "Legal/Law",
    "Manufacturing",
    "Marketing/Sales",
    "Military/Defense",
    "Non-profit/NGO",
    "Real Estate",
    "Retail/Customer Service",
    "Transportation/Logistics",
    "Other",
  ];

  const industryTypes = [
    "Technology",
    "Healthcare",
    "Financial Services",
    "Manufacturing",
    "Retail",
    "Education",
    "Government",
    "Energy",
    "Real Estate",
    "Transportation",
    "Telecommunications",
    "Media/Entertainment",
    "Agriculture",
    "Construction",
    "Hospitality",
    "Consulting",
    "Non-profit",
    "Other",
  ];

  const investmentRanges = [
    { value: "", label: "Select investment amount" },
    { value: "1000-5000", label: "$1,000 - $5,000" },
    { value: "5000-10000", label: "$5,000 - $10,000" },
    { value: "10000-25000", label: "$10,000 - $25,000" },
    { value: "25000-50000", label: "$25,000 - $50,000" },
    { value: "50000-100000", label: "$50,000 - $100,000" },
    { value: "100000-250000", label: "$100,000 - $250,000" },
    { value: "250000-500000", label: "$250,000 - $500,000" },
    { value: "500000+", label: "$500,000+" },
  ];

  const netWorthRanges = [
    { value: "", label: "Select net worth range" },
    { value: "0-25000", label: "$0 - $25,000" },
    { value: "25000-50000", label: "$25,000 - $50,000" },
    { value: "50000-100000", label: "$50,000 - $100,000" },
    { value: "100000-250000", label: "$100,000 - $250,000" },
    { value: "250000-500000", label: "$250,000 - $500,000" },
    { value: "500000-1000000", label: "$500,000 - $1,000,000" },
    { value: "1000000-5000000", label: "$1,000,000 - $5,000,000" },
    { value: "5000000+", label: "$5,000,000+" },
  ];

  const incomeRanges = [
    { value: "", label: "Select monthly income" },
    { value: "0-2000", label: "$0 - $2,000" },
    { value: "2000-5000", label: "$2,000 - $5,000" },
    { value: "5000-10000", label: "$5,000 - $10,000" },
    { value: "10000-20000", label: "$10,000 - $20,000" },
    { value: "20000-50000", label: "$20,000 - $50,000" },
    { value: "50000+", label: "$50,000+" },
  ];

  const sourceOfFundsOptions = [
    { value: "", label: "Select source of funds" },
    { value: "salary", label: "Salary/Employment Income" },
    { value: "business", label: "Business Income" },
    { value: "investments", label: "Investment Returns" },
    { value: "savings", label: "Personal Savings" },
    { value: "inheritance", label: "Inheritance" },
    { value: "gift", label: "Gift from Family" },
    { value: "property", label: "Property Sale" },
    { value: "pension", label: "Pension/Retirement Funds" },
    { value: "other", label: "Other" },
  ];

  const investmentPurposes = [
    { value: "", label: "Select investment purpose" },
    { value: "savings", label: "Long-term Savings" },
    { value: "diversification", label: "Portfolio Diversification" },
    { value: "trading", label: "Active Trading" },
    { value: "hedging", label: "Risk Management/Hedging" },
    { value: "speculation", label: "Speculation" },
    { value: "retirement", label: "Retirement Planning" },
    { value: "other", label: "Other" },
  ];

  const experienceLevels = [
    { value: "", label: "Select experience level" },
    { value: "none", label: "No Experience" },
    { value: "beginner", label: "Beginner (< 1 year)" },
    { value: "moderate", label: "Moderate (1-3 years)" },
    { value: "experienced", label: "Experienced (3-5 years)" },
    { value: "advanced", label: "Advanced (5+ years)" },
  ];

  // Calculate risk warnings
  const getInvestmentAmount = () => {
    if (!financial.estimatedInvestment) return 0;
    const range = financial.estimatedInvestment.split("-");
    return parseInt(range[0]) || 0;
  };

  const getNetWorth = () => {
    if (!financial.netWorth) return 0;
    const range = financial.netWorth.split("-");
    return parseInt(range[0]) || 0;
  };

  const isHighRiskInvestment = () => {
    const investmentAmount = getInvestmentAmount();
    const netWorth = getNetWorth();
    const riskPercentage = parseInt(financial.riskPercentage) || 0;

    return (
      investmentAmount > 100000 ||
      riskPercentage > 30 ||
      (netWorth > 0 && investmentAmount / netWorth > 0.25)
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Form>
        {/* Employment Information Section */}
        <Row>
          <Col md={12} className="mb-4">
            <h4 className="mb-3 text-primary">Employment Information</h4>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaBriefcase className="me-2 text-primary" />
                Current Occupation <span className="text-danger">*</span>
              </label>
              <select
                name="occupation"
                value={employment.occupation || ""}
                onChange={handleEmploymentChange}
                className={`form-control ${
                  errors.occupation ? "is-invalid" : ""
                }`}
                required
              >
                <option value="">Select your occupation</option>
                {occupationCategories.map((occupation) => (
                  <option key={occupation} value={occupation}>
                    {occupation}
                  </option>
                ))}
              </select>
              {errors.occupation && (
                <div className="invalid-feedback">{errors.occupation}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaUserTie className="me-2 text-primary" />
                Employment Status <span className="text-danger">*</span>
              </label>
              <select
                name="employmentStatus"
                value={employment.employmentStatus || ""}
                onChange={handleEmploymentChange}
                className={`form-control ${
                  errors.employmentStatus ? "is-invalid" : ""
                }`}
                required
              >
                {employmentStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.employmentStatus && (
                <div className="invalid-feedback">
                  {errors.employmentStatus}
                </div>
              )}
            </div>
          </Col>

          <Col md={12}>
            <div className="form-group">
              <label className="form-label">Job Title/Position</label>
              <input
                type="text"
                name="jobTitle"
                value={employment.jobTitle || ""}
                onChange={handleEmploymentChange}
                className="form-control"
                placeholder="Enter your job title or position"
              />
              <small className="form-text text-muted">
                E.g., Software Engineer, Marketing Manager, Business Owner
              </small>
            </div>
          </Col>

          <Col md={8}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaBuilding className="me-2 text-primary" />
                Employer/Company Name
                {(employment.employmentStatus === "employed" ||
                  employment.employmentStatus === "employed-part") && (
                  <span className="text-danger">*</span>
                )}
              </label>
              <input
                type="text"
                name="employerName"
                value={employment.employerName || ""}
                onChange={handleEmploymentChange}
                className={`form-control ${
                  errors.employerName ? "is-invalid" : ""
                }`}
                placeholder="Enter your employer or company name"
              />
              {errors.employerName && (
                <div className="invalid-feedback">{errors.employerName}</div>
              )}
              <small className="form-text text-muted">
                If self-employed, enter your business name or "Self-employed"
              </small>
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <select
                name="industry"
                value={employment.industry || ""}
                onChange={handleEmploymentChange}
                className="form-control"
              >
                <option value="">Select industry</option>
                {industryTypes.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">Work Location</label>
              <select
                name="workLocation"
                value={employment.workLocation || ""}
                onChange={handleEmploymentChange}
                className="form-control"
              >
                <option value="">Select work arrangement</option>
                <option value="office">Office-based</option>
                <option value="remote">Remote/Work from home</option>
                <option value="hybrid">Hybrid (Office + Remote)</option>
                <option value="field">Field work/Travel</option>
                <option value="other">Other</option>
              </select>
            </div>
          </Col>

          {/* Additional information for certain employment types */}
          {(employment.employmentStatus === "self-employed" ||
            employment.employmentStatus === "business-owner") && (
            <>
              <Col md={12}>
                <hr className="my-3" />
                <h6 className="mb-3">Business Information</h6>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">
                    Business Registration Number
                  </label>
                  <input
                    type="text"
                    name="businessRegNumber"
                    value={employment.businessRegNumber || ""}
                    onChange={handleEmploymentChange}
                    className="form-control"
                    placeholder="Enter business registration number"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">Years in Business</label>
                  <select
                    name="yearsInBusiness"
                    value={employment.yearsInBusiness || ""}
                    onChange={handleEmploymentChange}
                    className="form-control"
                  >
                    <option value="">Select...</option>
                    <option value="less-1">Less than 1 year</option>
                    <option value="1-2">1-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
              </Col>
            </>
          )}

          {employment.employmentStatus === "retired" && (
            <>
              <Col md={12}>
                <hr className="my-3" />
                <h6 className="mb-3">Retirement Information</h6>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">Previous Occupation</label>
                  <input
                    type="text"
                    name="previousOccupation"
                    value={employment.previousOccupation || ""}
                    onChange={handleEmploymentChange}
                    className="form-control"
                    placeholder="Your previous occupation before retirement"
                  />
                </div>
              </Col>

              <Col md={6}>
                <div className="form-group">
                  <label className="form-label">Year of Retirement</label>
                  <input
                    type="number"
                    name="retirementYear"
                    value={employment.retirementYear || ""}
                    onChange={handleEmploymentChange}
                    className="form-control"
                    placeholder="YYYY"
                    min="1950"
                    max={new Date().getFullYear()}
                  />
                </div>
              </Col>
            </>
          )}

          {/* Financial Information Section */}
          <Col md={12} className="my-4">
            <hr />
            <h4 className="mb-3 text-primary">Financial Information</h4>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaDollarSign className="me-2 text-primary" />
                Estimated Investment Amount{" "}
                <span className="text-danger">*</span>
              </label>
              <select
                name="estimatedInvestment"
                value={financial.estimatedInvestment || ""}
                onChange={handleFinancialChange}
                className={`form-control ${
                  errors.estimatedInvestment ? "is-invalid" : ""
                }`}
                required
              >
                {investmentRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
              {errors.estimatedInvestment && (
                <div className="invalid-feedback">
                  {errors.estimatedInvestment}
                </div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaPiggyBank className="me-2 text-primary" />
                Net Worth Range <span className="text-danger">*</span>
              </label>
              <select
                name="netWorth"
                value={financial.netWorth || ""}
                onChange={handleFinancialChange}
                className={`form-control ${
                  errors.netWorth ? "is-invalid" : ""
                }`}
                required
              >
                {netWorthRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
              {errors.netWorth && (
                <div className="invalid-feedback">{errors.netWorth}</div>
              )}
              <small className="form-text text-muted">
                Total assets minus total liabilities
              </small>
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">
                Average Monthly Income <span className="text-danger">*</span>
              </label>
              <select
                name="monthlyIncome"
                value={financial.monthlyIncome || ""}
                onChange={handleFinancialChange}
                className={`form-control ${
                  errors.monthlyIncome ? "is-invalid" : ""
                }`}
                required
              >
                {incomeRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
              {errors.monthlyIncome && (
                <div className="invalid-feedback">{errors.monthlyIncome}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">
                Source of Funds <span className="text-danger">*</span>
              </label>
              <select
                name="sourceOfFunds"
                value={financial.sourceOfFunds || ""}
                onChange={handleFinancialChange}
                className={`form-control ${
                  errors.sourceOfFunds ? "is-invalid" : ""
                }`}
                required
              >
                {sourceOfFundsOptions.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
              {errors.sourceOfFunds && (
                <div className="invalid-feedback">{errors.sourceOfFunds}</div>
              )}
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label">Investment Purpose</label>
              <select
                name="investmentPurpose"
                value={financial.investmentPurpose || ""}
                onChange={handleFinancialChange}
                className="form-control"
              >
                {investmentPurposes.map((purpose) => (
                  <option key={purpose.value} value={purpose.value}>
                    {purpose.label}
                  </option>
                ))}
              </select>
            </div>
          </Col>

          <Col md={6}>
            <div className="form-group">
              <label className="form-label d-flex align-items-center">
                <FaChartLine className="me-2 text-primary" />
                Investment Experience <span className="text-danger">*</span>
              </label>
              <select
                name="investmentExperience"
                value={financial.investmentExperience || ""}
                onChange={handleFinancialChange}
                className={`form-control ${
                  errors.investmentExperience ? "is-invalid" : ""
                }`}
                required
              >
                {experienceLevels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              {errors.investmentExperience && (
                <div className="invalid-feedback">
                  {errors.investmentExperience}
                </div>
              )}
            </div>
          </Col>

          <Col md={12}>
            <div className="form-group">
              <label className="form-label">
                Percentage of Funds Willing to Risk
              </label>
              <div className="d-flex align-items-center">
                <input
                  type="range"
                  name="riskPercentage"
                  value={financial.riskPercentage || 0}
                  onChange={handleFinancialChange}
                  className="form-range me-3"
                  min="0"
                  max="100"
                  step="5"
                />
                <span className="risk-percentage-display">
                  {financial.riskPercentage || 0}%
                </span>
              </div>
              <div className="d-flex justify-content-between text-muted small mt-1">
                <span>Conservative (0%)</span>
                <span>Moderate (50%)</span>
                <span>Aggressive (100%)</span>
              </div>
            </div>
          </Col>

          {financial.sourceOfFunds === "other" && (
            <Col md={12}>
              <div className="form-group">
                <label className="form-label">
                  Please specify source of funds
                </label>
                <input
                  type="text"
                  name="otherSourceOfFunds"
                  value={financial.otherSourceOfFunds || ""}
                  onChange={handleFinancialChange}
                  className="form-control"
                  placeholder="Please describe your source of funds"
                />
              </div>
            </Col>
          )}

          <Col md={12}>
            <div className="form-group">
              <label className="form-label">
                Additional Financial Information
              </label>
              <textarea
                name="additionalInfo"
                value={financial.additionalInfo || ""}
                onChange={handleFinancialChange}
                className="form-control"
                rows="3"
                placeholder="Any additional information about your financial situation (optional)"
              />
            </div>
          </Col>

          {/* Risk Warning */}
          {isHighRiskInvestment() && (
            <Col md={12}>
              <Alert variant="warning" className="mt-3">
                <FaExclamationTriangle className="me-2" />
                <strong>Enhanced Due Diligence Required</strong>
                <p className="mb-0 mt-2">
                  Based on your investment profile, additional documentation may
                  be required for verification. This may include bank
                  statements, proof of income, or source of wealth
                  documentation.
                </p>
              </Alert>
            </Col>
          )}
        </Row>
      </Form>
    </motion.div>
  );
};

export default EmploymentAndFinancialStep;
